import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { Users, Plus, Trophy, Loader2, Pencil, Check, X } from 'lucide-react';

export default function Teams() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner';

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '' });

  // Edit state: { teamId, name } or null
  const [editing, setEditing] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const load = () => {
    setLoading(true);
    api.getTeams().then((res) => setTeams(res.teams || [])).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.createTeam({ name: form.name.trim(), sport: 'soccer' });
      setForm({ name: '' });
      setShowForm(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (team) => {
    setEditing({ teamId: team.id, name: team.name });
    setEditError('');
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditError('');
  };

  const saveEdit = async () => {
    if (!editing) return;
    const trimmed = editing.name.trim();
    if (!trimmed) { setEditError('Team name cannot be empty'); return; }
    if (trimmed.length > 100) { setEditError('Team name must be 100 characters or fewer'); return; }

    // Client-side duplicate check
    const isDuplicate = teams.some(
      t => t.id !== editing.teamId && t.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) { setEditError('A team with that name already exists'); return; }

    setEditSaving(true);
    setEditError('');
    try {
      await api.updateTeam(editing.teamId, trimmed);
      setTeams(prev => prev.map(t => t.id === editing.teamId ? { ...t, name: trimmed } : t));
      setEditing(null);
    } catch (err) {
      setEditError(err.message || 'Failed to rename team');
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-navy-900">Teams</h1>
          <p className="text-gray-500 mt-1">Manage your squads</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-navy-900 hover:bg-navy-800 text-white px-4 py-2.5 rounded-xl transition-colors font-medium text-sm"
          >
            <Plus className="w-4 h-4" /> Add Team
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-navy-200 p-6 mb-6">
          <h2 className="text-navy-900 font-semibold mb-4">New Team</h2>
          <div>
            <label className="text-gray-500 text-sm mb-1 block">Team Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowForm(false); }}
              placeholder="e.g. France Olympic Team"
              autoFocus
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 text-sm"
            />
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleCreate}
              disabled={saving || !form.name.trim()}
              className="bg-navy-900 hover:bg-navy-800 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Team
            </button>
            <button
              onClick={() => { setShowForm(false); setForm({ name: '' }); }}
              className="bg-gray-100 hover:bg-gray-200 text-navy-900 px-5 py-2 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-500" /></div>
      ) : teams.length === 0 ? (
        <div className="text-center py-16">
          <Users className="text-gray-300 w-12 h-12 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No teams yet</p>
          <p className="text-gray-400 text-sm mt-1">Add your first team to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {teams.map((team) => {
            const isEditing = editing?.teamId === team.id;
            return (
              <div key={team.id} className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-navy-300 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Trophy className="text-navy-600 w-5 h-5" />
                  </div>

                  {isEditing ? (
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={editing.name}
                        onChange={(e) => { setEditing({ ...editing, name: e.target.value }); setEditError(''); }}
                        onKeyDown={handleEditKeyDown}
                        autoFocus
                        maxLength={100}
                        className="w-full border border-navy-400 rounded-lg px-3 py-1.5 text-navy-900 text-sm focus:outline-none focus:border-navy-700"
                      />
                      {editError && <p className="text-red-500 text-xs mt-1">{editError}</p>}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={saveEdit}
                          disabled={editSaving}
                          className="flex items-center gap-1 bg-navy-900 hover:bg-navy-800 disabled:opacity-50 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                        >
                          {editSaving
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Check className="w-3 h-3" />}
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-navy-700 px-3 py-1 rounded text-xs transition-colors"
                        >
                          <X className="w-3 h-3" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-navy-900 font-semibold truncate">{team.name}</h3>
                        <p className="text-gray-500 text-sm">{team.sport || 'Soccer'}</p>
                      </div>
                      {canEdit && (
                        <button
                          onClick={() => startEdit(team)}
                          title="Rename team"
                          className="flex-shrink-0 p-1.5 text-gray-400 hover:text-navy-700 hover:bg-navy-50 rounded-lg transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
